import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';

interface WebPushState {
  isSupported: boolean;
  isSubscribed: boolean;
  isSubscribing: boolean;
  permission: NotificationPermission;
}

export const useWebPush = () => {
  const [state, setState] = useState<WebPushState>({
    isSupported: 'serviceWorker' in navigator && 'PushManager' in window,
    isSubscribed: false,
    isSubscribing: false,
    permission: 'Notification' in window ? Notification.permission : 'denied'
  });

  const { user, token } = useAuth();

  // Obtener clave pública VAPID
  const getVapidPublicKey = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/push/vapid-public-key`);
      const data = await response.json();
      return data.success ? data.data.publicKey : null;
    } catch (error) {
      console.error('Error obteniendo clave VAPID:', error);
      return null;
    }
  }, []);

  // Convertir clave VAPID a formato UInt8Array
  const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // Verificar si ya está suscrito
  const checkSubscription = useCallback(async () => {
    if (!state.isSupported || !user) return;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return;

      const subscription = await registration.pushManager.getSubscription();
      setState(prev => ({ ...prev, isSubscribed: !!subscription }));
    } catch (error) {
      console.error('Error verificando suscripción:', error);
    }
  }, [state.isSupported, user]);

  // Suscribirse a notificaciones push
  const subscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported || !user || !token) {
      console.warn('Web Push no soportado o usuario no autenticado');
      return false;
    }

    setState(prev => ({ ...prev, isSubscribing: true }));

    try {
      // Solicitar permisos de notificación
      const permission = await Notification.requestPermission();
      setState(prev => ({ ...prev, permission }));

      if (permission !== 'granted') {
        console.warn('Permisos de notificación denegados');
        return false;
      }

      // Obtener clave VAPID
      const vapidPublicKey = await getVapidPublicKey();
      if (!vapidPublicKey) {
        console.error('No se pudo obtener la clave VAPID');
        return false;
      }

      // Obtener registro del service worker
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        console.error('Service Worker no registrado');
        return false;
      }

      // Crear suscripción
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Enviar suscripción al servidor
      const response = await fetch(`${process.env.REACT_APP_API_URL}/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subscription })
      });

      const data = await response.json();
      if (data.success) {
        setState(prev => ({ ...prev, isSubscribed: true }));
        console.log('✅ Suscripción a notificaciones push exitosa');
        return true;
      } else {
        console.error('Error guardando suscripción:', data.message);
        return false;
      }

    } catch (error) {
      console.error('Error suscribiéndose a push notifications:', error);
      return false;
    } finally {
      setState(prev => ({ ...prev, isSubscribing: false }));
    }
  }, [state.isSupported, user, token, getVapidPublicKey]);

  // Desuscribirse de notificaciones push
  const unsubscribe = useCallback(async (): Promise<boolean> => {
    if (!state.isSupported || !user || !token) return false;

    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) return false;

      const subscription = await registration.pushManager.getSubscription();
      if (!subscription) return true;

      // Desuscribirse localmente
      const unsubscribed = await subscription.unsubscribe();
      if (!unsubscribed) return false;

      // Notificar al servidor
      await fetch(`${process.env.REACT_APP_API_URL}/push/unsubscribe`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ endpoint: subscription.endpoint })
      });

      setState(prev => ({ ...prev, isSubscribed: false }));
      console.log('✅ Desuscripción exitosa');
      return true;

    } catch (error) {
      console.error('Error desuscribiéndose:', error);
      return false;
    }
  }, [state.isSupported, user, token]);

  // Enviar notificación de prueba
  const sendTestNotification = useCallback(async (): Promise<boolean> => {
    if (!user || !token) return false;

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/push/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      return data.success;
    } catch (error) {
      console.error('Error enviando notificación de prueba:', error);
      return false;
    }
  }, [user, token]);

  // Verificar suscripción al cargar
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Auto-suscribirse si el usuario tiene recordatorios con push habilitado
  useEffect(() => {
    if (state.isSupported && user && !state.isSubscribed && !state.isSubscribing) {
      // Auto-suscribirse después de un breve delay para no interferir con la carga inicial
      const timer = setTimeout(() => {
        subscribe();
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [state.isSupported, state.isSubscribed, state.isSubscribing, user, subscribe]);

  return {
    ...state,
    subscribe,
    unsubscribe,
    sendTestNotification,
    checkSubscription
  };
};